import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as newsService from '../../../services/newsService';
import { getFileUrl } from '../../../utils/urlUtils';
import { useAuth } from '../../../context/AuthContext';
import { AppRoles } from '../../../services/authService';
import styles from './NewsList.module.css';

const { Search } = Input;
const { Title, Text } = Typography;

const renderLanguages = (instances = []) => instances
  .map(item => item.countryLanguage)
  .filter(Boolean)
  .join(' · ');

/**
 * NewsList displays creator/admin News CRUD rows and admin-only creator assignment management.
 */
const NewsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(AppRoles.Admin);

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [assignedCreators, setAssignedCreators] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [availableCreators, setAvailableCreators] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [creatorPagination, setCreatorPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [availablePagination, setAvailablePagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchNews = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await newsService.getNews({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
      });
      setNews(response.items || []);
      setPagination(prev => ({ ...prev, current: response.page, total: response.totalCount }));
    } catch (error) {
      message.error(t('news.fetchError', 'Failed to load News'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, t]);

  const fetchAssignedCreators = useCallback(async (page = 1, search = '') => {
    if (!isAdmin) return;
    setAssignedLoading(true);
    try {
      const response = await newsService.getAssignedNewsCreators({ page, pageSize: creatorPagination.pageSize, search: search || undefined });
      setAssignedCreators(response.items || []);
      setCreatorPagination(prev => ({ ...prev, current: response.page, total: response.totalCount }));
    } catch (error) {
      message.error(t('news.creatorFetchError', 'Failed to load News creators'));
    } finally {
      setAssignedLoading(false);
    }
  }, [creatorPagination.pageSize, isAdmin, t]);

  const fetchAvailableCreators = useCallback(async (page = 1, search = '') => {
    setAvailableLoading(true);
    try {
      const response = await newsService.getAvailableNewsCreators({ page, pageSize: availablePagination.pageSize, search: search || undefined });
      setAvailableCreators(response.items || []);
      setAvailablePagination(prev => ({ ...prev, current: response.page, total: response.totalCount }));
    } catch (error) {
      message.error(t('news.availableCreatorFetchError', 'Failed to load available creators'));
    } finally {
      setAvailableLoading(false);
    }
  }, [availablePagination.pageSize, t]);

  useEffect(() => {
    fetchNews();
    // Initial load only; callbacks already read current pagination/search state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) fetchAssignedCreators();
    // Load creator assignments when admin mode becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleTogglePublish = async (item) => {
    try {
      if (item.isPublished) {
        await newsService.unpublishNews(item.id);
        message.success(t('news.unpublishSuccess', 'News unpublished'));
      } else {
        await newsService.publishNews(item.id);
        message.success(t('news.publishSuccess', 'News published'));
      }
      fetchNews(pagination.current);
    } catch (error) {
      message.error(error.message || t('news.publishError', 'Failed to update News status'));
    }
  };

  const handleDelete = async (item) => {
    try {
      await newsService.deleteNews(item.id);
      message.success(t('news.deleteSuccess', 'News deleted'));
      fetchNews(pagination.current);
    } catch (error) {
      message.error(error.message || t('news.deleteError', 'Failed to delete News'));
    }
  };

  const handleCopyPublicLink = (item) => {
    const url = `${window.location.origin}${newsService.getNewsPublicPath(item)}`;
    navigator.clipboard.writeText(url);
    message.success(t('news.linkCopied', 'News link copied'));
  };

  const handleAssignCreator = async (userId) => {
    try {
      await newsService.assignNewsCreator(userId);
      message.success(t('news.creatorAssigned', 'News creator assigned'));
      fetchAssignedCreators();
      fetchAvailableCreators(availablePagination.current);
    } catch (error) {
      message.error(error.message || t('news.creatorAssignError', 'Failed to assign creator'));
    }
  };

  const handleRevokeCreator = async (userId) => {
    try {
      await newsService.revokeNewsCreator(userId);
      message.success(t('news.creatorRevoked', 'News creator assignment revoked'));
      fetchAssignedCreators(creatorPagination.current);
    } catch (error) {
      message.error(error.message || t('news.creatorRevokeError', 'Failed to revoke creator'));
    }
  };

  const newsColumns = [
    {
      title: t('news.article', 'News'),
      dataIndex: 'name',
      width: 350,
      render: (_, item) => (
        <div className={styles.newsTitle} onClick={() => navigate(`/admin/news/${item.id}`)}>
          <Avatar shape="square" size={54} src={getFileUrl(item.coverImageUrl)} icon={<FileTextOutlined />} />
          <div>
            <button type="button" className={styles.newsLink} onClick={() => navigate(`/admin/news/${item.id}`)}>
              {item.name}
            </button>
            <div className={styles.metaLine}>{renderLanguages(item.instances)}</div>
          </div>
        </div>
      ),
    },
    {
      title: t('news.status', 'Status'),
      width: 140,
      render: (_, item) => (
        <Space wrap>
          <Tag color={item.isPublished ? 'green' : 'default'} icon={item.isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
            {item.isPublished ? t('news.published', 'Published') : t('news.draft', 'Draft')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('news.owner', 'Owner'),
      dataIndex: 'ownerName',
      width: 220,
      render: (value) => value || t('common.unknown', 'Unknown'),
    },
    {
      title: t('news.visits', 'Visits'),
      dataIndex: 'visitCount',
      width: 110,
    },
    {
      title: t('common.updated', 'Updated'),
      dataIndex: 'dateModified',
      width: 180,
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('common.actions', 'Actions'),
      width: 280,
      render: (_, item) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('common.edit', 'Edit')}><Button type="text" icon={<EditOutlined />} onClick={() => navigate(`/admin/news/${item.id}`)} /></Tooltip>
          <Tooltip title={item.isPublished ? t('news.unpublish', 'Unpublish') : t('news.publish', 'Publish')}>
            <Button type="text" icon={item.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />} onClick={() => handleTogglePublish(item)} />
          </Tooltip>
          <Tooltip title={t('news.copyLink', 'Copy public link')}><Button type="text" icon={<CopyOutlined />} onClick={() => handleCopyPublicLink(item)} /></Tooltip>
          <Tooltip title={t('news.openPublic', 'Open public page')}><Button type="text" icon={<EyeOutlined />} onClick={() => window.open(newsService.getNewsPublicPath(item), '_blank')} /></Tooltip>
          <Popconfirm title={t('news.deleteConfirm', 'Delete this News item?')} onConfirm={() => handleDelete(item)} okText={t('common.delete', 'Delete')}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const creatorColumns = [
    {
      title: t('news.creator', 'Creator'),
      render: (_, item) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <Text strong>{item.displayName || item.email}</Text>
            <div className={styles.metaLine}>{item.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('news.assignedBy', 'Assigned by'),
      dataIndex: 'assignedByName',
    },
    {
      title: t('news.assignedAt', 'Assigned at'),
      dataIndex: 'assignedAt',
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('common.actions', 'Actions'),
      width: 120,
      render: (_, item) => (
        <Popconfirm title={t('news.revokeCreatorConfirm', 'Revoke this News creator assignment?')} onConfirm={() => handleRevokeCreator(item.userId)}>
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const availableColumns = [
    {
      title: t('news.availableCreator', 'Available creator'),
      render: (_, item) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <Text strong>{item.displayName || item.email}</Text>
            <div className={styles.metaLine}>{item.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('common.actions', 'Actions'),
      width: 120,
      render: (_, item) => (
        <Tooltip title={t('news.assign', 'Assign')}>
          <Button type="text" icon={<UserAddOutlined />} onClick={() => handleAssignCreator(item.userId)} />
        </Tooltip>
      ),
    },
  ];

  const articlesTab = (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <Search placeholder={t('news.searchPlaceholder', 'Search News')} allowClear onSearch={(value) => { setSearchText(value); fetchNews(1, value); }} className={styles.search} />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchNews(pagination.current)}>{t('common.refresh', 'Refresh')}</Button>
          {!isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/news/new')}>{t('news.createNews', 'Create News')}</Button>}
        </Space>
      </div>
      {isAdmin && <div className={styles.adminNotice}>{t('news.adminCreateNotice', 'Admins supervise News and assign creators. Assigned content-creators create and own News items.')}</div>}
      <Table
        rowKey="id"
        columns={newsColumns}
        dataSource={news}
        loading={loading}
        pagination={pagination}
        onChange={(p) => fetchNews(p.current)}
        locale={{ emptyText: <Empty description={t('news.noNews', 'No News yet')} /> }}
        scroll={{ x: 980 }}
      />
    </div>
  );

  const creatorsTab = isAdmin ? (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <Search placeholder={t('news.searchCreators', 'Search assigned creators')} allowClear onSearch={(value) => fetchAssignedCreators(1, value)} className={styles.search} />
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => { setAssignModalOpen(true); fetchAvailableCreators(); }}>
          {t('news.assignCreator', 'Assign Creator')}
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={creatorColumns}
        dataSource={assignedCreators}
        loading={assignedLoading}
        pagination={creatorPagination}
        onChange={(p) => fetchAssignedCreators(p.current)}
      />
    </div>
  ) : null;

  return (
    <PagesWrapper>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <FileTextOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>{t('news.managementTitle', 'News Management')}</Title>
          </div>
          <div>
            <Text type="secondary">{t('news.managementSubtitle', 'Create multilingual News, manage publishing, and review public reach.')}</Text>
          </div>
        </div>
        <Tabs
          items={[
            { key: 'articles', label: <span><FileTextOutlined /> {t('news.articles', 'Articles')}</span>, children: articlesTab },
            ...(isAdmin ? [{ key: 'creators', label: <span><TeamOutlined /> {t('news.creators', 'Creators')}</span>, children: creatorsTab }] : []),
          ]}
        />
      </div>
      <Modal
        title={t('news.assignCreator', 'Assign News Creator')}
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        footer={null}
        width={760}
      >
        <Search placeholder={t('news.searchAvailableCreators', 'Search available creators')} allowClear onSearch={(value) => fetchAvailableCreators(1, value)} className={styles.modalSearch} />
        <Table
          rowKey="userId"
          columns={availableColumns}
          dataSource={availableCreators}
          loading={availableLoading}
          pagination={availablePagination}
          onChange={(p) => fetchAvailableCreators(p.current)}
        />
      </Modal>
    </PagesWrapper>
  );
};

export default NewsList;
